import { useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { purgeOtherClients, useClients } from '@/api/queries'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Overlay'
import { CountBadge } from '@/components/ui/Badge'
import { accentFor } from '@/lib/accent'
import { cn } from '@/lib/cn'

/** Sub-route of `/c/:clientId/...`, so switching keeps you on the same page. */
function subRouteOf(pathname: string): string {
  const match = /^\/c\/[^/]+\/(.+)$/.exec(pathname)
  return match?.[1] ?? 'crawl'
}

/**
 * Command-palette-style switcher. Landing on the same sub-route under the new
 * client is the difference between a switcher people use and one they avoid
 * (docs/02-app-shell.md §5).
 */
export function ClientSwitcher({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { clientId } = useParams()
  const { data: clients = [] } = useClients()
  const navigate = useNavigate()
  const location = useLocation()
  const qc = useQueryClient()
  const [query, setQuery] = useState('')

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return clients
      .filter((c) => c.id !== clientId)
      .filter(
        (c) =>
          !needle ||
          c.name.toLowerCase().includes(needle) ||
          c.primaryDomain?.toLowerCase().includes(needle),
      )
  }, [clients, clientId, query])

  function switchTo(nextId: string) {
    // Purge before navigating so client A's rows cannot render under B for a
    // frame (docs/04-frontend-architecture.md §4).
    purgeOtherClients(qc, nextId)
    localStorage.setItem('wbm.lastClientId', nextId)
    navigate(`/c/${nextId}/${subRouteOf(location.pathname)}`)
    setQuery('')
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Switch client"
      description="You land on the same section under the client you pick."
      width="max-w-[520px]"
      footer={
        <Button size="sm" onClick={onClose}>
          Cancel
        </Button>
      }
    >
      <div className="space-y-3 pb-2">
        <Input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search clients"
          aria-label="Search clients"
        />
        <ul className="max-h-[320px] overflow-y-auto">
          {matches.length === 0 && (
            <li className="px-1 py-3 text-[13px] text-ink-faint">No other clients match.</li>
          )}
          {matches.map((client) => (
            <li key={client.id}>
              <button
                type="button"
                onClick={() => switchTo(client.id)}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-[4px] px-2 py-2 text-left',
                  'hover:bg-surface-sunk',
                )}
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: accentFor(client.id) }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-head text-[13px] font-semibold">
                    {client.name}
                  </span>
                  <span className="block truncate font-mono text-[11px] text-ink-faint">
                    {client.primaryDomain}
                  </span>
                </span>
                <CountBadge count={client.pendingCount ?? 0} />
              </button>
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  )
}
